
import React, { createContext, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthError, AuthResponse, User, Session } from '@supabase/supabase-js';
import { SecurityEnhancedAuth } from '@/components/auth/SecurityEnhancedAuth';
import { useAuthState } from '@/hooks/use-auth-state';
import { AuthFormData } from '@/types';
import { localLogger } from '@/lib/local-logger';
import { logger } from '@/utils/logger';
import { useNotificationsStore, useOfflineStore } from '@/stores';
import { queueCoordinator } from '@/services/queue-coordinator';
import { distributedLockManager } from '@/services/distributed-lock-manager';

export interface AuthContextValue {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string, redirectTo?: string) => Promise<AuthResponse>;
  signUp: (data: AuthFormData, redirectTo?: string) => Promise<AuthResponse>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ data: any; error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ data: any; error: AuthError | null }>;
  error: string | null;
  clearError: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { user, session, isLoading, isAuthenticated } = useAuthState();
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Supabase emits SIGNED_IN/INITIAL_SESSION with a fresh user object each time, so this
  // effect keys on the id to avoid re-running the whole bootstrap on every auth event.
  const userId = user?.id ?? null;
  const userEmail = user?.email ?? null;

  React.useEffect(() => {
    if (userId) {
      localLogger.setUser({
        id: userId,
        email: userEmail ?? undefined,
      });
      localLogger.addBreadcrumb('User authenticated', 'auth', 'info');

      // Initialize Zustand stores
      useNotificationsStore.getState().initialize(userId);
      useOfflineStore.getState().refreshPendingRecordings(userId);

      // Start distributed lock cleanup service (prevents cross-tab duplicate processing)
      distributedLockManager.startCleanup();

      // Start transcription retry monitor
      import('@/services/transcription-retry-monitor').then(({ retryMonitor }) => {
        retryMonitor.startMonitoring(userId);
        // console.log('[Auth] Started transcription retry monitor');
      });

      // Start AI stuck detection service (monitors for incomplete AI generations)
      import('@/services/ai-stuck-detector').then(({ startAIStuckDetector }) => {
        startAIStuckDetector();
        // console.log('[Auth] Started AI stuck detector');
      });

      // Add focus listener to process queue when app regains focus
      const handleWindowFocus = () => {
        logger.debug('[Auth] App regained focus - triggering queue processing');
        queueCoordinator.processQueue(userId, 'app_focus').catch((error) => {
          logger.error('[Auth] Failed to process queue on focus:', error);
          // Silently fail - don't disrupt user experience
        });
      };

      window.addEventListener('focus', handleWindowFocus);

      return () => {
        window.removeEventListener('focus', handleWindowFocus);
      };
    } else {
      // Clear user context
      localLogger.clearSession();
      localLogger.addBreadcrumb('User signed out', 'auth', 'info');

      // Cleanup Zustand stores
      useNotificationsStore.getState().cleanup();
      useOfflineStore.getState().reset();

      // Stop distributed lock cleanup service and release all locks
      distributedLockManager.stopCleanup();
      // Note: We don't release all locks here - let them expire naturally
      // This allows other tabs to continue processing

      // Stop transcription retry monitor
      import('@/services/transcription-retry-monitor').then(({ retryMonitor }) => {
        retryMonitor.stopMonitoring();
        // console.log('[Auth] Stopped transcription retry monitor');
      });

      // Stop AI stuck detection service
      import('@/services/ai-stuck-detector').then(({ stopAIStuckDetector }) => {
        stopAIStuckDetector();
        // console.log('[Auth] Stopped AI stuck detector');
      });
    }
  }, [userId, userEmail]);

  const clearError = () => setError(null);

  const handleAuthError = (error: AuthError) => {
    let errorMessage = error.message || 'An unknown error occurred';

    // Handle specific Supabase auth errors
    if (errorMessage.includes('Invalid login credentials')) {
      errorMessage = 'Invalid email or password. Please try again.';
    } else if (errorMessage.includes('Email not confirmed')) {
      errorMessage = 'Please verify your email address before signing in.';
    } else if (errorMessage.includes('Too many requests')) {
      errorMessage = 'Too many attempts. Please wait a moment before trying again.';
    } else if (errorMessage.includes('User already registered')) {
      errorMessage = 'An account with this email already exists. Please sign in instead.';
    }

    // Capture auth errors locally
    localLogger.captureError(new Error(errorMessage), {
      operation: 'authentication',
      metadata: { authError: error },
    });

    setError(errorMessage);
  };

  const signIn = async (email: string, password: string, redirectTo?: string) => {
    clearError();
    try {
      const response = await SecurityEnhancedAuth.signInWithPassword(email, password);

      if (response.error) {
        handleAuthError(response.error);
      } else if (response.data?.user) {
        if (redirectTo) {
          navigate(redirectTo);
        } else {
          navigate('/');
        }
      }

      return response;
    } catch (error) {
      const authError = error as AuthError;
      handleAuthError(authError);
      throw error;
    }
  };

  const signUp = async (data: AuthFormData, redirectTo?: string) => {
    clearError();
    try {
      const response = await SecurityEnhancedAuth.signUp(data.email, data.password, {
        full_name: data.fullName,
        company: data.company,
        industry: data.industry,
      });

      if (response.error) {
        handleAuthError(response.error);
      } else {
        // For successful signup, we don't redirect immediately
        // The user needs to confirm their email first
      }

      return response;
    } catch (error) {
      const authError = error as AuthError;
      handleAuthError(authError);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await SecurityEnhancedAuth.signOut();
      navigate('/auth');
    } catch (error) {
      const authError = error as AuthError;
      handleAuthError(authError);
    }
  };

  const resetPassword = async (email: string) => {
    clearError();
    try {
      const response = await SecurityEnhancedAuth.resetPassword(email);

      if (response.error) {
        handleAuthError(response.error);
      }

      return response;
    } catch (error) {
      const authError = error as AuthError;
      handleAuthError(authError);
      throw error;
    }
  };

  const updatePassword = async (newPassword: string) => {
    clearError();
    try {
      const response = await SecurityEnhancedAuth.updatePassword(newPassword);

      if (response.error) {
        handleAuthError(response.error);
      } else {
        navigate('/');
      }

      return response;
    } catch (error) {
      const authError = error as AuthError;
      handleAuthError(authError);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      isLoading,
      isAuthenticated,
      signIn,
      signUp,
      signOut,
      resetPassword,
      updatePassword,
      error,
      clearError
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export { useAuth } from './use-auth';
